import { MediumLargeHeadingStyled } from "src/components/custom/texts/texts.styled";
import { Card, Form, Input, Table, Skeleton } from "antd";
import { ColumnsType } from "antd/es/table";
import React, { useEffect, useState } from "react";
import { getImportDataPage } from "../../../Api/content";
import { useLocale } from "../../../i18n/localeContext";
import { StrapiError } from "../../StrapiError";
import { _translateValue } from "../translation-gestor/translateValue";

const SCREEN_NAME = "PantallaCarrerasImport";

function CareersImport({
  isLoading,
  getData,
  data,
}: {
  isLoading: boolean;
  getData: (params: { rowsCount: number; page: number; nombre: string }) => void;
  data: any;
}) {
  const { locale } = useLocale();
  const [importDataPage, setImportDataPage] = React.useState<any>(null);
  const [isLoadingContent, setIsLoadingContent] = React.useState<boolean>(true);
  const [hasError, setHasError] = useState(false);
  const [pageNumber, setPageNumber] = React.useState(1);
  const [rowsCount, setRowsCount] = React.useState(5);
  const [careerName, setCareerName] = React.useState("");
  const [translatedCareers, setTranslatedCareers] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    getData({ rowsCount, page: pageNumber, nombre: careerName });
  }, [careerName, rowsCount, pageNumber, getData]);

  // Efecto para traducir los nombres de carreras cuando lleguen los datos o cambie el locale
  useEffect(() => {
    if (data?.data?.result && Array.isArray(data.data.result)) {
      const translateCareers = async () => {
        const translations: { [key: string]: string } = {};
        
        // Procesar cada carrera para traducir su nombre según el locale actual
        const translationPromises = data.data.result.map(async (career: any) => {
          if (career.carreraNombre) {
            // Pasar el locale actual a la función de traducción
            const translated = await _translateValue(SCREEN_NAME, career.carreraNombre, locale);
            translations[career.carreraNombre] = translated;
          }
        });

        await Promise.all(translationPromises);
        setTranslatedCareers(translations);
      };

      translateCareers();
    }
  }, [data?.data?.result, locale]);

  useEffect(() => {
    let cancel = false;
    setHasError(false);
    setIsLoadingContent(true);
    
    const timeoutId = setTimeout(() => {
      if (!cancel) {
        setImportDataPage(null);
        setIsLoadingContent(false);
        setHasError(locale === 'en');
      }
    }, 3000);
    
    getImportDataPage(locale)
      .then((res) => {
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          clearTimeout(timeoutId);
          setImportDataPage(data);
          setIsLoadingContent(false);
        }
      })
      .catch(() => {
        if (!cancel) {
          clearTimeout(timeoutId);
          setImportDataPage(null);
          setIsLoadingContent(false);
          setHasError(locale === 'en');
        }
      });
    return () => {
      cancel = true;
      clearTimeout(timeoutId);
    };
  }, [locale]);

  const columns = React.useMemo<ColumnsType<any>>(
    () => [
      {
        title: importDataPage?.importData_table?.[0]?.label || "Carrera no.",
        dataIndex: "carreraId",
      },
      {
        title: importDataPage?.importData_table?.[1]?.label || "Carrera",
        dataIndex: "carreraNombre",
        render: (text: string) => {
          // Usar la traducción si existe, sino usar el texto original
          return translatedCareers[text] || text;
        },
      },
    ],
    [importDataPage, translatedCareers],
  );

  if (hasError && locale === 'en') {
    return <StrapiError 
      onRetry={() => {
        setHasError(false);
        setIsLoadingContent(true);
      }}
      onUseDefault={() => {
        setHasError(false);
        setIsLoadingContent(false);
        setImportDataPage(null);
      }}
    />;
  }

  return (
    <>
      {isLoadingContent ? (
        <>
          <Skeleton.Input active size="large" style={{ width: 250, marginBottom: 16 }} />
          <Skeleton.Input active size="default" style={{ width: 300, marginBottom: 16 }} />
          <Card bodyStyle={{ padding: 0, width: "100%" }}>
            <Skeleton active paragraph={{ rows: 6 }} />
          </Card>
        </>
      ) : (
        <>
          <MediumLargeHeadingStyled>
            {importDataPage?.subtitle || "Carreras importadas"}
          </MediumLargeHeadingStyled>

          <Form.Item label={importDataPage?.search_input || "Búsqueda por nombre de carrera"}>
            <Input
              type="text"
              onChange={(e) => setCareerName(e.target.value)}
              placeholder={importDataPage?.search_input || "Búsqueda por nombre de carrera"}
            />
          </Form.Item>

          <Card bodyStyle={{ padding: 0, width: "100%" }}>
            <Table<any>
              loading={isLoading}
              columns={columns}
              dataSource={data?.data?.result}
              rowKey={(v) => v.carreraId}
              pagination={{
                onChange(page, pageSize) {
                  setPageNumber(page);
                  setRowsCount(pageSize);
                },
                pageSize: rowsCount,
                current: pageNumber,
                total: data?.data?.pageCount * rowsCount,
                showSizeChanger: true,
                style: { marginRight: "30px" },
              }}
              scroll={{ x: 800 }}
            />
          </Card>
        </>
      )}
    </>
  );
}

export default CareersImport;
